import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation } from "react-query";
import { PageHeaderSection } from "../../components/common/PageHeadSection";
import _ from "lodash";
import { tabModel, TabPanel } from "../../components/elements/TabPanel";
import { HotelSelect } from "../../components/common/HotelSelect";
import { LanguageDropdown } from "../../components/common/LanguageDropdown";
import { enmDirection } from "../../models/enums/Direction";
import { mdlLanguage } from "../../models/ui-models/Language";
import { HotelAccountSettings } from "../../components/fact-sheet/HotelAccountSettings";
import { FactSheetInfos } from "../../components/fact-sheet/FactSheetInfos";
import { FactSheetReport } from "../../components/fact-sheet/FactSheetReport";
import { setPdfExportObject } from "../../store/SiteSlice";
import { enmPdfExportType } from "../../models/enums/PdfExportType";
import { mdlFactSheetReportContent } from "../../models/ui-models/FactSheetReportContent";
import { mdlPDFRendererProps } from "../../models/ui-models/PDFRendererProps";
import { enmPdfRendererType } from "../../models/enums/PdfRenderer";
import { FactSheetService } from "../../services/FactSheetService";
import { mdlFactSheetCategory } from "../../models/ui-models/FactSheetCategory";
import { mdlFactSheetGetCategoriesRequest } from "../../models/service-models/fact-sheet/FactSheetGetCategoriesRequest";
import { useAppDispatch } from "../../store/Hooks";
import { mdlFactSheetTemplateRequest } from "../../models/service-models/fact-sheet/FactSheetTemplateRequest";
import { mdlFactSheetTemplate } from "../../models/domain/FactSheetTemplate";
import { enmFactSheetTemplateType } from "../../models/enums/FactSheetTemplateType";
import { mdlGiataRequest } from "../../models/ui-models/GiataRequest";
import dayjs from "dayjs";
import { mdlFactSheetItemUpdateInsertRequest } from "../../models/service-models/fact-sheet/FactSheetItemUpdateInsertRequest";
import { FactSheetRoomInfos } from "../../components/fact-sheet/FactSheetRoomInfos";
import { mdlBaseHotelIdRequest } from "../../models/service-models/common/BaseHotelIdRequest";
import { mdlFactSheetRoomItem } from "../../models/service-models/fact-sheet/FactSheetRoomItem";
import FactSheetLanguageDropdown from "../../components/fact-sheet/FactSheetLanguageDropdown";

export const FactSheetDashboard = () => {
    const { t } = useTranslation();
    const [activeTab, setActiveTab] = useState<number>(0);
    const [selectedLanguage, setSelectedLanguage] = useState<mdlLanguage>();
    const [selectedHotel, setSelectedHotel] = useState<string>();
    const [activeCell, setActiveCell] = useState<string>();
    const [activeTemplate, setActiveTemplate] = useState<mdlFactSheetReportContent>();
    const [selectedCateogry, setSelectedCategory] = useState<mdlFactSheetCategory>();
    const [categories, setCategories] = useState<mdlFactSheetCategory[]>();
    const [tabModels, setTabModels] = useState<tabModel[]>();
    const [templateData, setTemplateData] = useState<mdlFactSheetTemplate>();
    const [giataData, setGiataData] = useState<mdlGiataRequest>();
    const [roomInfos, setRoomInfos] = useState<mdlFactSheetRoomItem[]>();
    const dispatch = useAppDispatch();

    useEffect(() => {
        if (selectedLanguage && selectedHotel) {
            handleHotelSelect(selectedHotel);
        }
        if (!giataData) {
            const fetchIp = async () => {
                const userIp = await getUserIp();
                setGiataData({ ipAddress: userIp, date: dayjs().format("DD.MM.YYYY HH:mm") } as mdlGiataRequest);
            };
            fetchIp();
        }
        if (selectedHotel) {
            setMutateCategoryData(selectedHotel, (data: mdlFactSheetCategory[]) => {
                setCategories(data);
                handleTabModels(data, undefined);
            });
        }
    }, [selectedHotel, selectedCateogry, selectedLanguage]);
    const handleTabModels = (data: mdlFactSheetCategory[], _selectedCategory: mdlFactSheetCategory | undefined, _selectedHotel?: string) => {
        let tabItems = [
            {
                label: t("factSheet.hotelAccountSettings"),
                code: `hotelAccountSettings-${selectedHotel}`,
                display: true,
                content: <HotelAccountSettings selectedHotel={_selectedHotel ?? selectedHotel} selectedLanguage={selectedLanguage} readonly={true} />
            }
        ];

        data.map((x: mdlFactSheetCategory) => {
            if (x.name != null) {
                tabItems.push(
                    {
                        label: x.name,
                        code: `${x.name}-${selectedHotel}`,
                        display: true,
                        content: <FactSheetInfos updateTemplateData={updateTemplateData} selectedCateogry={_selectedCategory ?? selectedCateogry} fnActiveCellChange={handleChangeActiveCell} activeCell={activeCell} selectedHotel={_selectedHotel ?? selectedHotel} selectedLanguage={selectedLanguage} />
                    });
            }
        })

        tabItems.push(
            {
                label: t("factSheet.roomInfos.title"),
                code: `factSheetRoomInfos-${selectedHotel}`,
                display: true,
                content: <FactSheetRoomInfos tableItems={roomInfos} selectedLanguage={selectedLanguage} activeCell={activeCell} selectedHotel={selectedHotel} />
            });

        tabItems.push(
            {
                label: t("factSheet.giataFactSheetReport"),
                code: `giataFactSheetReport-${selectedHotel}`,
                display: true,
                content: <FactSheetReport giataData={giataData} handlePrintGiata={handlePrintGiata} data={templateData} handlePrint={handlePrint} selectedLanguage={selectedLanguage} selectedHotel={selectedHotel} activeTemplate={activeTemplate} handleActiveTemplate={(item: mdlFactSheetReportContent) => { setActiveTemplate(item) }} />
            });

        setTabModels(tabItems);
    }
    const updateTemplateData = (request: mdlFactSheetItemUpdateInsertRequest) => {
        if (selectedHotel)
            prepareTemplateData(selectedHotel, enmFactSheetTemplateType.LUXURY)
    }
    const getUserIp = async (): Promise<string> => {
        try {
            const response = await fetch('https://api.ipify.org?format=json');
            const data = await response.json();
            return data.ip; // IP adresini döndür
        } catch (error) {
            console.error("IP adresi alınırken hata oluştu:", error);
            return ''; // Hata durumunda boş bir string döndür
        }
    };

    const prepareTemplateData = (hotelId: string, item: enmFactSheetTemplateType) => {
        setTemplateData(undefined);
        handleFactSheetTemplate(hotelId, item, (data: mdlFactSheetTemplate) => {
            setTemplateData(data);
        });
    };

    const getRoomInfos = useMutation({
        mutationFn: (request: mdlBaseHotelIdRequest) => {
            return FactSheetService.GetRoomItems(request)
        }
    })

    const setMutateRoomInfos = async (request: mdlBaseHotelIdRequest, fnSuccess: Function) => {
        const data = await getRoomInfos.mutateAsync(request)
        if (data && data.body && (data.body as any).data && (data.body as any).data.grids) {
            fnSuccess((data.body as any).data.grids);
        }
    }

    const getTemplateData = useMutation({
        mutationFn: (request: mdlFactSheetTemplateRequest) => {
            return FactSheetService.GetTemplateData(request)
        }
    })

    const setMutateTemplateData = async (request: mdlFactSheetTemplateRequest, fnSuccess: Function) => {
        const data = await getTemplateData.mutateAsync(request)
        if (data && data.body && data.body.data) {
            fnSuccess(data.body.data);
        }
    }
    const handlePrint = (selectedHotel: string, rendererType: enmPdfRendererType) => {
        var exportProps: mdlPDFRendererProps = {
            params: { selectedHotel: selectedHotel, data: templateData, selectedLanguage: selectedLanguage, printMode: true },
            rendererType: rendererType,
            type: enmPdfExportType.PRINT
        }
        dispatch(setPdfExportObject(exportProps));
    }

    const handlePrintGiata = (data: mdlGiataRequest) => {
        var exportProps: mdlPDFRendererProps = {
            params: { data: data, printMode: true },
            rendererType: enmPdfRendererType.FactSheetGiata,
            type: enmPdfExportType.PRINT
        }
        dispatch(setPdfExportObject(exportProps));
    }

    const handleFactSheetTemplate = (selectedHotel: string, templateType: enmFactSheetTemplateType, fnFunction: Function) => {
        setMutateTemplateData({ currentLanguage: selectedLanguage?.value, hotelId: selectedHotel, templateType: templateType } as mdlFactSheetTemplateRequest, (data: mdlFactSheetTemplate) => {
            fnFunction(data);
        });
    }

    const handleHotelSelect = (hotelId: string) => {
        setSelectedHotel(hotelId);
        setTabModels([]);
        setRoomInfos([])
        setMutateCategoryData(hotelId, (data: mdlFactSheetCategory[]) => {
            prepareTemplateData(hotelId, enmFactSheetTemplateType.LUXURY);
            setCategories(data);
            setMutateRoomInfos({ hotelId: hotelId, currentLanguage: selectedLanguage?.value } as mdlBaseHotelIdRequest, (roomInfoData: mdlFactSheetRoomItem[]) => {
                setRoomInfos(roomInfoData);
                handleTabModels(data, undefined, hotelId);
            })
        });
    }

    const handleLanguageSelect = (lang: string) => {
        setSelectedLanguage({ value: lang, text: lang } as mdlLanguage);
        setActiveTab(0);
    }

    const handleChangeActiveTab = (index: number) => {
        setActiveTab(index);
        setSelectedCategory(undefined)
        let category: mdlFactSheetCategory | undefined = undefined;
        if (categories && tabModels && index > 0 && index < tabModels.length - 1) {
            category = (categories.find(x => x.name == _.split(tabModels[index].code, '-')[0]));
        }
        setSelectedCategory(category)
        categories && handleTabModels(categories, category);
    };
    const handleChangeActiveCell = (id: string) => {
        setActiveCell(id);
    };
    const exportToPdf = (template: enmPdfRendererType) => {
        var exportProps: mdlPDFRendererProps = {
            params: { data: [] },
            rendererType: template ?? activeTemplate?.template,
            type: enmPdfExportType.PDF
        }
        dispatch(setPdfExportObject(exportProps));
    }

    const getCategories = useMutation({
        mutationFn: (hotelId: string) => {
            return FactSheetService.GetCategories({ hotelId: hotelId, currentLanguage: selectedLanguage?.value } as mdlFactSheetGetCategoriesRequest)
        }
    })

    const setMutateCategoryData = async (hotelId: string, fnSuccess: Function) => {
        const data = await getCategories.mutateAsync(hotelId)
        if (data && data.body && data.body.data) {
            fnSuccess(data.body.data);
        }
    }


    return (
        <>
            <div className="nk-content ">
                <div className="container-fluid">
                    <div className="nk-content-inner">
                        <div className="nk-content-body">
                            <PageHeaderSection
                                pageTitle={t("factSheet.title")}
                            />
                            <div className="nk-block">
                                <div className="card card-bordered h-100">
                                    <div className="card-inner">
                                        <div className="row">
                                            <div className="col-6">
                                                <HotelSelect onChange={handleHotelSelect} />
                                            </div>
                                            <div className="col-6 text-end aligin-items-end">
                                                <label>
                                                    <FactSheetLanguageDropdown onChange={handleLanguageSelect} />
                                                </label>
                                            </div>
                                            {
                                                selectedLanguage &&
                                                <div className="col-12">
                                                    {
                                                        tabModels &&
                                                        <TabPanel
                                                            activeTab={activeTab}
                                                            fnChangeActiveTab={handleChangeActiveTab}
                                                            tabItems={tabModels}
                                                            autoShowFirstItem={true}
                                                        />
                                                    }
                                                </div>
                                            }
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>


    );
};
function dispatch(arg0: { payload: any; type: "site/setPdfExportObject"; }) {
    throw new Error("Function not implemented.");
}

